/* eslint-disable no-unused-vars */
import { store } from 'redux/store';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

import Promisable from './promisable.service';
import http from './http.service';
import WorkOrderService from './workorder.service';
import InspectionService from './inspection.service';
import ChecksService from './checks.service';
import TasksService from './tasks.service';

import { tasksListsActions } from '@redux/slices/tasks';
import { getAppDispatch } from '@utilities/dispatch.util';
import checkTokenIsGoingToExpire from '@utilities/token-expiry-check.util';
import { cacheActions } from '@redux/slices/cache';
import { prepareInspectionForBackend } from 'transformers/inspection';
import { prepareNewAndEditedEquipmentForBackend } from 'transformers/equipment';
import AuthService from './auth.service';
import { prepareHSEEventsForBackend } from 'transformers/hse-event';
import { prepareWorkOrderForBackend } from 'transformers/work-order';
import HSEEventService from './hse-event.service';
import {
  getPhotosAndDocumentsDataFromSelectedInspection,
  addSignatureId,
  setSyncFailedAssets,
  removeSuccessfullySyncedRetryAsset
} from './assets.service';
import MobileSyncCountService from './mobile-sync-count.service';
import { prepareCheckForBackend } from 'transformers/check';
import {
  deleteAssetInIndexDB,
  getAssetDataBase64InIndexDB
} from '@utilities/photos-documents.util';
import EquipmentService from './equipment.service';
import { jurisdictionNumbersActions } from '@redux/slices/jurisdiction-numbers';
import { revertUpdatedJurisdictionNumbersListAndMap } from '@components/add-edit-equipment/helper';
import { hseEventActions } from '@redux/slices/hse-events';
import { inspectionActions } from '@redux/slices/inspection';
import { checkActions } from '@redux/slices/checks';
import { deleteNewPSVRelation } from './cache-services/json-manipulation/psvs';
import SettingsService from './settings.service';
import BookmarksService from './bookmarks.service';

let syncPhotos = 0,
  syncDocument = 0,
  workOrderNumber = 0,
  workRequestNumber = 0,
  inspectionNumber = 0,
  hseNumber = 0,
  newEquipmentAssets = 0,
  editedEquipmentAssets = 0,
  inspectionPhotoCount = 0,
  workOrderPhotoCount = 0,
  workRequestPhotoCount = 0,
  incidentPhotoCount = 0,
  inspectionDocumentCount = 0,
  workOrderDocumentCount = 0,
  workRequestDocumentCount = 0,
  incidentDocumentCount = 0,
  checkNumber = 0,
  checkPhotoCount = 0,
  checkDocumentCount = 0,
  failedImageCount = 0,
  failedDocumentCount = 0,
  failedImages = [],
  failedDocuments = [],
  retryInspectionsCount = 0,
  retryWorkOrdersCount = 0,
  retryWorkRequestsCount = 0,
  retryIncidentCOunt = 0,
  retryCheckCount = 0,
  retryPhotoCount = 0,
  retryDocumentCount = 0,
  retryNewEquipmentAssetsCount = 0,
  retryEditedEquipmentAssetsCount = 0;

const SyncingService = {
  inspectionAndWorkOrderService: async ({
    currentTask,
    sync,
    doneNumber,
    pendingSyncLength,
    type,
    singleSync = false,
    syncAssetsList,
    retry = false
  }) => {
    failedImages = [];
    failedDocuments = [];
    const dispatch = getAppDispatch();
    http.setJWT();
    if (doneNumber === 1 || retry) {
      syncPhotos = 0;
      syncDocument = 0;
      workOrderNumber = 0;
      workRequestNumber = 0;
      inspectionNumber = 0;
      hseNumber = 0;
      newEquipmentAssets = 0;
      editedEquipmentAssets = 0;
      inspectionPhotoCount = 0;
      workOrderPhotoCount = 0;
      workRequestPhotoCount = 0;
      incidentPhotoCount = 0;
      inspectionDocumentCount = 0;
      workOrderDocumentCount = 0;
      workRequestDocumentCount = 0;
      incidentDocumentCount = 0;
      failedImageCount = 0;
      failedDocumentCount = 0;
      checkNumber = 0;
      checkPhotoCount = 0;
      checkDocumentCount = 0;
      failedImages = [];
      failedDocuments = [];
    }
    const {
      inspection,
      auth,
      cache,
      tasks,
      mobileSyncCount,
      jurisdictionNumbers,
      settings,
      bookmarks
    } = store.getState();
    const { updatedSettings } = settings;
    const { bookmarks: bookmarksList } = bookmarks;
    const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
    const { idToStartSync } = mobileSyncCount;
    const { equipmentsMap, equipments: cacheEquipments = [] } = cache;
    const { syncFailedAssets } = tasks;

    const {
      failureCriteria,
      inspectionFormsUserCache,
      passFailList,
      passDeficiencyFail,
      inspectionTypesMap,
      equipmentFormsUserCache = [],
      equipmentFormsUserCacheMap = {}
    } = inspection;
    const { formData, psvs = [] } = addSignatureId(currentTask?.formData || {});
    const { photos, documents, passFailTrue, passFailNA, passFailFalse, inspectionResultAnswer } =
      getPhotosAndDocumentsDataFromSelectedInspection(formData || {});
    currentTask =
      !currentTask?.inspectableItemId && currentTask?.equipmentId
        ? {
            ...currentTask,
            siteId: cacheEquipments[equipmentsMap[currentTask?.equipmentId]]?.siteId || null
          }
        : currentTask;

    let bookmarksData = {
      isSettingUpdated: false,
      bookmarkThatNotSync: [],
      updateBookmarks: [],
      deletedBookmarks: []
    };
    if (singleSync) {
      const [settingsUpdated] = await SettingsService.updateSettings(updatedSettings);
      bookmarksData.isSettingUpdated = settingsUpdated;
      if (settingsUpdated) {
        bookmarksData.bookmarkThatNotSync =
          bookmarksList?.filter((bookmark) => !bookmark?.sync) || [];
        bookmarksData.updateBookmarks =
          bookmarksList?.filter((bookmark) => bookmark?.sync && bookmark?.update) || [];
        bookmarksData.deletedBookmarks =
          bookmarksList?.filter((bookmark) => bookmark?.isDelete) || [];
      }
    }
    if (type === 'Equipment')
      await syncNewAndEditedEquipment({
        currentTask,
        pendingSyncLength,
        doneNumber,
        sync,
        dispatch,
        equipmentFormsUserCache,
        equipmentFormsUserCacheMap,
        jurisdictionNumbersMap,
        jurisdictionNumbers,
        bookmarksData
      });

    if (type === 'Inspection')
      await syncInspection({
        photos,
        documents,
        currentTask,
        formData,
        inspectionFormsUserCache,
        pendingSyncLength,
        doneNumber,
        sync,
        dispatch,
        singleSync,
        failureCriteria,
        passFailTrue,
        passFailNA,
        passFailFalse,
        inspectionResultAnswer,
        passFailList,
        passDeficiencyFail,
        psvs,
        idToStartSync,
        equipmentsMap,
        cacheEquipments,
        bookmarksData
      });
    else if (type === 'Work Order')
      await syncWorkOrders({
        photos,
        documents,
        currentTask,
        inspectionFormsUserCache,
        pendingSyncLength,
        doneNumber,
        sync,
        dispatch,
        singleSync,
        idToStartSync,
        equipmentsMap,
        cacheEquipments,
        bookmarksData
      });
    else if (type === 'HSE')
      await syncHseEvents({
        photos,
        documents,
        currentTask,
        formData,
        inspectionFormsUserCache,
        incidentCategoryMap: inspection?.incidentCategoryMap,
        pendingSyncLength,
        doneNumber,
        sync,
        dispatch,
        inspectionTypesMap,
        idToStartSync,
        bookmarksData
      });
    else if (type === 'Check') {
      await syncCheckEvent({
        photos,
        documents,
        currentTask,
        formData,
        inspectionFormsUserCache,
        incidentCategoryMap: inspection?.incidentCategoryMap,
        pendingSyncLength,
        doneNumber,
        sync,
        dispatch,
        inspectionTypesMap,
        idToStartSync,
        failureCriteria,
        inspectionResultAnswer,
        passFailList,
        passDeficiencyFail,
        passFailFalse,
        equipmentsMap,
        cacheEquipments,
        bookmarksData
      });
    } else if (type == 'syncFailedAsset') {
      await syncFailedPhotosAndDocuments({
        syncFailedAssets: syncAssetsList,
        dispatch,
        latestSyncFailedAssets: syncFailedAssets
      });
    }

    if (type !== 'syncFailedAsset')
      setSyncFailedAssets({
        syncFailedAssets,
        dispatch,
        photo: failedImages,
        document: failedDocuments
      });

    // console.log('Hello World', {
    //   retryInspectionsCount,
    //   retryWorkOrdersCount,
    //   retryWorkRequestsCount,
    //   retryIncidentCOunt,
    //   retryCheckCount,
    //   retryPhotoCount,
    //   retryDocumentCount
    // });

    dispatch?.(
      tasksListsActions.setRetryAssetCount({
        retryInspectionsCount,
        retryWorkOrdersCount,
        retryWorkRequestsCount,
        retryIncidentCOunt,
        retryCheckCount,
        retryPhotoCount,
        retryDocumentCount,
        retryNewEquipmentAssetsCount,
        retryEditedEquipmentAssetsCount
      })
    );
  }
};

export default SyncingService;

const syncHseEvents = async ({
  photos,
  documents,
  currentTask,
  formData,
  inspectionFormsUserCache,
  incidentCategoryMap,
  pendingSyncLength,
  doneNumber,
  sync,
  dispatch,
  inspectionTypesMap,
  idToStartSync,
  bookmarksData
}) => {
  const inspectionAllPhotos = photos?.filter((d) => d?.content) || [];
  const inspectionAllDocuments = documents?.filter((d) => d?.content) || [];
  const transformData = prepareHSEEventsForBackend({
    data: currentTask,
    formData,
    inspectionFormsUserCache,
    incidentCategoryMap,
    inspectionTypesMap
  });
  // console.log({ transformData, inspectionAllPhotos, inspectionAllDocuments });
  const [response] = await Promisable.asPromise(
    http.post(`/Incidents/SubmitIncident`, {
      ...transformData
    })
  );
  const { data = {}, status } = response || {};
  if (!_.isEmpty(data) && status === 201) {
    const id = data;
    ++hseNumber;

    if (currentTask?.failedTask) {
      ++retryIncidentCOunt;
    }

    incrementSyncTasksRecordsAndAssets({ dispatch });
    for (let photo of inspectionAllPhotos) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: photo?.content?.id });

      const photoBody = {
        ...photo,
        content: photoBase64,
        fileName: `${uuidv4()}-${photo?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'Incident'
      };

      try {
        const photoApiResponse = await http.post(`/Photos`, photoBody, {
          timeout: 130000
        });
        if (photoApiResponse?.status === 201) {
          ++syncPhotos;
          ++incidentPhotoCount;
          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: photo?.content?.id });

          if (currentTask?.failedTask) {
            ++retryPhotoCount;
          }
        } else {
          throw new Error('photo not sync');
        }
      } catch (error) {
        failedImages = [
          ...failedImages,
          {
            ...photoBody,
            content: { id: photo?.content?.id },
            idToStartSync,
            previousCount: incidentPhotoCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    for (let document of inspectionAllDocuments) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: document?.content?.id });

      const documentBody = {
        ...document,
        content: photoBase64,
        fileName: `${uuidv4()}-${document?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'Incident'
      };
      try {
        const documentApiResponse = await http.post(`/Documents`, documentBody, {
          timeout: 130000
        });
        if (documentApiResponse?.status === 201) {
          ++syncDocument;
          ++incidentDocumentCount;
          if (currentTask?.failedTask) {
            ++retryDocumentCount;
          }
          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: document?.content?.id });
        } else {
          throw new Error('document not sync');
        }
      } catch (error) {
        failedDocuments = [
          ...failedDocuments,
          {
            ...documentBody,
            content: { id: document?.content?.id },
            id: uuidv4(),
            idToStartSync,
            previousCount: incidentDocumentCount
          }
        ];
        console.error('something wrong to send data');
      }
    }
    await HSEEventService.deleteNewHSEEvent(id);
    dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));
    if (sync) await HSEEventService.getAllHseEvents({ sync, initialLogin: false });
  } else {
    // failedIncidentCOunt.push(transformData.incidentId);
    dispatch?.(
      hseEventActions.updateNewHSEEvent({
        hseEventId: transformData.incidentId,
        data: {
          ...currentTask,
          failedTask: true
        }
      })
    );
  }

  if (bookmarksData?.isSettingUpdated) {
    await BookmarksService.getSyncAndLatestBookmarkOnSIngleTaskSync({
      bookmarks: bookmarksData?.bookmarkThatNotSync,
      updateBookmarks: bookmarksData?.updateBookmarks,
      deletedBookmarks: bookmarksData?.deletedBookmarks
    });
  }

  return true;
};

const syncNewAndEditedEquipment = async ({
  currentTask,
  pendingSyncLength,
  doneNumber,
  sync,
  dispatch,
  equipmentFormsUserCache,
  equipmentFormsUserCacheMap,
  jurisdictionNumbersMap,
  jurisdictionNumbers,
  bookmarksData
}) => {
  const transformData = prepareNewAndEditedEquipmentForBackend({
    asset: currentTask,
    equipmentFormsUserCache,
    equipmentFormsUserCacheMap,
    jurisdictionNumbersMap
  });

  // console.log({ transformData, currentTask, jurisdictionNumbersMap });

  if (currentTask?.isNew) {
    await syncNewEquipment({
      currentTask,
      transformData,
      dispatch,
      pendingSyncLength,
      doneNumber,
      sync
    });
  } else {
    await syncExistingEquipment({
      currentTask,
      transformData,
      dispatch,
      pendingSyncLength,
      doneNumber,
      sync,
      jurisdictionNumbersMap,
      jurisdictionNumbers
    });
  }
  if (bookmarksData?.isSettingUpdated) {
    await BookmarksService.getSyncAndLatestBookmarkOnSIngleTaskSync({
      bookmarks: bookmarksData?.bookmarkThatNotSync,
      updateBookmarks: bookmarksData?.updateBookmarks,
      deletedBookmarks: bookmarksData?.deletedBookmarks
    });
  }

  return true;
};

const syncWorkOrders = async ({
  photos,
  documents,
  currentTask,
  inspectionFormsUserCache,
  pendingSyncLength,
  doneNumber,
  sync,
  dispatch,
  singleSync,
  idToStartSync,
  equipmentsMap,
  cacheEquipments,
  bookmarksData
}) => {
  const workOrderOrRequestPhotos =
    [...currentTask.photos, ...photos]?.filter((d) => d?.content) || [];
  const workOrderOrPhotosDocuments =
    [...currentTask.documents, ...documents]?.filter((d) => d?.content) || [];
  const woId = currentTask.workorderId || uuidv4();
  const workRequestOrderOrder = prepareWorkOrderForBackend({
    newWorkRequestOrderOrder: {
      ...currentTask,
      description: currentTask?.tasksDescriptionWIthHTMLTags || currentTask?.description || ''
    },
    inspectionFormsUserCache: inspectionFormsUserCache,
    equipmentsMap,
    cacheEquipments
  });
  // console.log({
  //   data: {
  //     ...workRequestOrderOrder,
  //     woId
  //   },
  //   workOrderOrRequestPhotos,
  //   workOrderOrPhotosDocuments,
  //   currentTask
  // });

  const [response] = await Promisable.asPromise(
    http.post(`/WorkOrderOrRequest`, {
      ...workRequestOrderOrder,
      woId
    })
  );
  const { data = {}, status } = response || {};
  if (!_.isEmpty(data) && status === 201) {
    const id = data;
    if (currentTask.entity !== 'WorkRequest') {
      ++workOrderNumber;
      if (currentTask?.failedTask) {
        ++retryWorkOrdersCount;
      }
    }
    if (currentTask.entity === 'WorkRequest') {
      ++workRequestNumber;
      if (currentTask?.failedTask) {
        ++retryWorkRequestsCount;
      }
    }

    incrementSyncTasksRecordsAndAssets({ dispatch });
    for (let photo of workOrderOrRequestPhotos) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: photo?.content?.id });

      const photoBody = {
        ...photo,
        content: photoBase64,
        fileName: `${uuidv4()}-${photo?.fileName}`,
        objectId: id,
        childId: id
      };

      try {
        const photoApiResponse = await http.post(`/Photos`, photoBody, {
          timeout: 130000
        });
        if (photoApiResponse?.status === 201) {
          currentTask.entity === 'WorkRequest' ? workRequestPhotoCount++ : workOrderPhotoCount++;
          ++syncPhotos;

          if (currentTask?.failedTask) {
            ++retryPhotoCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: photo?.content?.id });
        } else {
          throw new Error('photo not sync');
        }
      } catch (error) {
        failedImages = [
          ...failedImages,
          {
            ...photoBody,
            content: { id: photo?.content?.id },
            idToStartSync,
            previousCount:
              currentTask.entity === 'WorkRequest' ? workRequestPhotoCount : workOrderPhotoCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    for (let document of workOrderOrPhotosDocuments) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: document?.content?.id });

      const documentBody = {
        ...document,
        fileName: `${uuidv4()}-${document?.fileName}`,
        objectId: id,
        childId: id,
        content: photoBase64
      };

      try {
        const documentApiResponse = await http.post(`/Documents`, documentBody, {
          timeout: 130000
        });
        if (documentApiResponse?.status === 201) {
          syncDocument++;
          currentTask.entity === 'WorkRequest'
            ? workRequestDocumentCount++
            : workOrderDocumentCount++;

          if (currentTask?.failedTask) {
            ++retryDocumentCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: document?.content?.id });
        } else {
          throw new Error('document not sync');
        }
      } catch (error) {
        failedDocuments = [
          ...failedDocuments,
          {
            ...documentBody,
            id: uuidv4(),
            content: { id: document?.content?.id },
            idToStartSync,
            previousCount:
              currentTask.entity === 'WorkRequest'
                ? workRequestDocumentCount
                : workOrderDocumentCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    await WorkOrderService.deleteWorkOrderAndRequest(id);
    dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));

    if (sync) {
      await TasksService.getAllTasksList(sync, false, singleSync);
    }
  } else {
    // if (currentTask.entity === 'WorkRequest') failedWorkRequestsCount.push(currentTask.workorderId);
    // else failedWorkOrdersCount.push(currentTask.workorderId);
    if (currentTask.entity === 'WorkRequest') {
      dispatch?.(
        tasksListsActions.updateNewWorkOrder1({
          id: currentTask.workorderId
        })
      );
    } else {
      dispatch?.(
        tasksListsActions.updateNewWorkOrder1({
          id: currentTask.workorderId
        })
      );
    }
  }

  if (bookmarksData?.isSettingUpdated) {
    await BookmarksService.getSyncAndLatestBookmarkOnSIngleTaskSync({
      bookmarks: bookmarksData?.bookmarkThatNotSync,
      updateBookmarks: bookmarksData?.updateBookmarks,
      deletedBookmarks: bookmarksData?.deletedBookmarks
    });
  }

  return true;
};

const syncInspection = async ({
  photos,
  documents,
  currentTask,
  formData,
  inspectionFormsUserCache,
  pendingSyncLength,
  doneNumber,
  sync,
  dispatch,
  singleSync,
  failureCriteria,
  passFailTrue,
  passFailNA,
  passFailFalse,
  inspectionResultAnswer,
  passFailList,
  passDeficiencyFail,
  psvs,
  idToStartSync,
  equipmentsMap,
  cacheEquipments,
  bookmarksData
}) => {
  const inspectionAllPhotos = photos?.filter((d) => d?.content) || [];
  const inspectionAllDocuments = documents?.filter((d) => d?.content) || [];

  const { transformData, cmlList } = prepareInspectionForBackend(
    currentTask,
    failureCriteria,
    inspectionFormsUserCache,
    passFailTrue,
    passFailNA,
    passFailFalse,
    inspectionResultAnswer,
    passFailList,
    passDeficiencyFail,
    formData,
    psvs,
    equipmentsMap,
    cacheEquipments
  );

  // console.log({ transformData, currentTask, singleSync, cmlList });

  const [response] = await Promisable.asPromise(
    http.post(`/DetailInspection`, {
      ...transformData
    })
  );

  const { data = {}, status } = response || {};

  // CML sync
  let isCMlsOfInspectionSyncedSuccessfully = false;
  if (cmlList?.length > 0) {
    const [responseOfCML] = await Promisable.asPromise(http.post(`/InspectionCmls`, cmlList));

    const { data = {}, status } = responseOfCML || {};

    if (!_.isEmpty(data) && status === 201) {
      isCMlsOfInspectionSyncedSuccessfully = true;
    } else {
      isCMlsOfInspectionSyncedSuccessfully = false;
    }
  } else {
    isCMlsOfInspectionSyncedSuccessfully = true;
  }

  if (!_.isEmpty(data) && status === 201 && isCMlsOfInspectionSyncedSuccessfully) {
    const id = data;
    ++inspectionNumber;

    if (currentTask?.failedTask) {
      ++retryInspectionsCount;
    }

    incrementSyncTasksRecordsAndAssets({ dispatch });

    for (let photo of inspectionAllPhotos) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: photo?.content?.id });
      const photoBody = {
        ...photo,
        content: photoBase64,
        fileName: `${uuidv4()}-${photo?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'Inspection'
      };

      try {
        const photoApiResponse = await http.post(`/Photos`, photoBody, {
          timeout: 130000
        });
        if (photoApiResponse?.status === 201) {
          syncPhotos++;
          inspectionPhotoCount++;

          if (currentTask?.failedTask) {
            ++retryPhotoCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: photo?.content?.id });
        } else {
          throw new Error('photo not sync');
        }
        // throw new Error('sd');
      } catch (error) {
        failedImages = [
          ...failedImages,
          {
            ...photoBody,
            content: { id: photo?.content?.id },
            idToStartSync,
            previousCount: inspectionPhotoCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    for (let document of inspectionAllDocuments) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: document?.content?.id });

      const documentBody = {
        ...document,
        content: photoBase64,
        fileName: `${uuidv4()}-${document?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'Inspection'
      };
      try {
        const documentApiResponse = await http.post(`/Documents`, documentBody, {
          timeout: 130000
        });
        if (documentApiResponse?.status === 201) {
          syncDocument++;
          inspectionDocumentCount++;
          if (currentTask?.failedTask) {
            ++retryDocumentCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: document?.content?.id });
        } else {
          throw new Error('document not sync');
        }
      } catch (error) {
        failedDocuments = [
          ...failedDocuments,
          {
            ...documentBody,
            content: { id: document?.content?.id },
            id: uuidv4(),
            idToStartSync,
            previousCount: inspectionDocumentCount
          }
        ];

        console.error('something wrong to send data');
      }
    }

    await InspectionService.deleteNewInspection(id);
    await InspectionService.deleteInspectionCML(currentTask.inspectableItemId || currentTask.id);
    dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));
    if (sync) {
      await InspectionService.getAllInspectionList(sync, false, singleSync);
    }
  } else {
    // failedInspectionsCount.push(currentTask.id);

    dispatch?.(
      inspectionActions.updateNewInspection({
        inspectionId: currentTask.id,
        data: {
          ...currentTask,
          failedTask: true
        }
      })
    );
  }

  if (bookmarksData?.isSettingUpdated) {
    await BookmarksService.getSyncAndLatestBookmarkOnSIngleTaskSync({
      bookmarks: bookmarksData?.bookmarkThatNotSync,
      updateBookmarks: bookmarksData?.updateBookmarks,
      deletedBookmarks: bookmarksData?.deletedBookmarks
    });
  }

  return true;
};

const syncCheckEvent = async ({
  photos,
  documents,
  currentTask,
  formData,
  inspectionFormsUserCache,
  pendingSyncLength,
  doneNumber,
  sync,
  dispatch,
  singleSync,
  idToStartSync,
  failureCriteria,
  inspectionResultAnswer,
  passFailList,
  passDeficiencyFail,
  passFailFalse,
  equipmentsMap,
  cacheEquipments,
  bookmarksData
}) => {
  // Error
  const inspectionAllPhotos = photos?.filter((d) => d?.content) || [];
  const inspectionAllDocuments = documents?.filter((d) => d?.content) || [];
  const transformData = prepareCheckForBackend({
    data: currentTask,
    inspectionFormsUserCache,
    formData,
    failureCriteria,
    inspectionResultAnswer,
    passFailList,
    passDeficiencyFail,
    passFailFalse,
    equipmentsMap,
    cacheEquipments
  });
  const [response] = await Promisable.asPromise(
    http.post(`/AssetChecks`, {
      ...transformData
    })
  );

  const { data = {}, status } = response || {};
  if (!_.isEmpty(data) && status === 201) {
    const id = data;
    ++checkNumber;
    if (currentTask?.failedTask) {
      ++retryCheckCount;
    }

    incrementSyncTasksRecordsAndAssets({ dispatch });

    for (let photo of inspectionAllPhotos) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: photo?.content?.id });

      const photoBody = {
        ...photo,
        fileName: `${uuidv4()}-${photo?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'AssetCheck',
        content: photoBase64
      };

      try {
        const photoApiResponse = await http.post(`/Photos`, photoBody, {
          timeout: 130000
        });
        if (photoApiResponse?.status === 201) {
          syncPhotos++;
          checkPhotoCount++;

          if (currentTask?.failedTask) {
            ++retryPhotoCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: photo?.content?.id });
        } else {
          throw new Error('photo not sync');
        }
        // throw new Error('sd');
      } catch (error) {
        failedImages = [
          ...failedImages,
          {
            ...photoBody,
            content: { id: photo?.content?.id },
            idToStartSync,
            previousCount: checkPhotoCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    for (let document of inspectionAllDocuments) {
      const photoBase64 = await getAssetDataBase64InIndexDB({ assetId: document?.content?.id });

      const documentBody = {
        ...document,
        content: photoBase64,
        fileName: `${uuidv4()}-${document?.fileName}`,
        objectId: id,
        childId: id,
        documentType: 'AssetCheck'
      };
      try {
        const documentApiResponse = await http.post(`/Documents`, documentBody, {
          timeout: 130000
        });
        if (documentApiResponse?.status === 201) {
          syncDocument++;
          checkDocumentCount++;

          if (currentTask?.failedTask) {
            ++retryDocumentCount;
          }

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: document?.content?.id });
        } else {
          throw new Error('document not sync');
        }
      } catch (error) {
        failedDocuments = [
          ...failedDocuments,
          {
            ...documentBody,
            content: { id: document?.content?.id },
            id: uuidv4(),
            idToStartSync,
            previousCount: checkDocumentCount
          }
        ];
        console.error('something wrong to send data');
      }
    }

    await ChecksService.deleteNewCheck({ checkId: id });
    dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));
    if (sync) {
      await ChecksService.getAllChecksEvents(sync, false, singleSync);
    }
  } else {
    // failedCheckCount.push(currentTask.id);

    dispatch?.(
      checkActions.updateNewCheck({
        checkId: currentTask.id,
        data: {
          ...currentTask,
          failedTask: true
        }
      })
    );
  }

  if (bookmarksData?.isSettingUpdated) {
    await BookmarksService.getSyncAndLatestBookmarkOnSIngleTaskSync({
      bookmarks: bookmarksData?.bookmarkThatNotSync,
      updateBookmarks: bookmarksData?.updateBookmarks,
      deletedBookmarks: bookmarksData?.deletedBookmarks
    });
  }
  return true;
};

const syncFailedPhotosAndDocuments = async ({
  syncFailedAssets,
  dispatch,
  latestSyncFailedAssets
}) => {
  const photoIds = [],
    documentIds = [];
  const photos = syncFailedAssets?.photos || [];
  const documents = syncFailedAssets?.documents || [];

  let groupPhotosByMobileSyncCountId = photos?.reduce((acc, curr) => {
    acc[curr.idToStartSync] = acc[curr.idToStartSync]
      ? [...acc[curr.idToStartSync], { ...curr }]
      : [{ ...curr }];
    return acc;
  }, {});

  let groupDocumentByMobileSyncCountId = documents?.reduce((acc, curr) => {
    acc[curr.idToStartSync] = acc[curr.idToStartSync]
      ? [...acc[curr.idToStartSync], { ...curr }]
      : [{ ...curr }];
    return acc;
  }, {});

  for (let uniqueIdOfMobileSync of Object.keys(groupPhotosByMobileSyncCountId)) {
    let inspectionPhotoRetryCount = 0,
      workOrderPhotoRetryCount = 0,
      workRequestPhotoRetryCount = 0,
      incidentPhotoRetryCount = 0,
      inspectionPhotoPreviousCount = 0,
      workOrderPhotoPreviousCount = 0,
      workRequestPhotoPreviousCount = 0,
      incidentPhotoPreviousCount = 0;
    for (let photo of groupPhotosByMobileSyncCountId[uniqueIdOfMobileSync]) {
      const photoBase64 = await getAssetDataBase64InIndexDB({
        assetId: photo?.content?.id
      });

      try {
        const {
          old: removeOldField,
          previousCount: removeObj1,
          idToStartSync: removeObj2,
          ...body
        } = photo;

        const photoApiResponse = await http.post(
          `/Photos`,
          {
            ...body,
            content: photoBase64
          },
          {
            timeout: 130000
          }
        );
        if (photoApiResponse?.status === 201) {
          failedImageCount++;
          photoIds.push(photo.id);

          ++retryPhotoCount;

          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: photo?.content?.id });

          if (body?.documentType === 'Inspection') {
            ++inspectionPhotoRetryCount;
            inspectionPhotoPreviousCount = photo?.previousCount;
          }
          if (body?.documentType === 'Incident') {
            ++incidentPhotoRetryCount;
            incidentPhotoPreviousCount = photo?.previousCount;
          }
          if (body?.documentType === 'WorkOrder') {
            ++workOrderPhotoRetryCount;
            workOrderPhotoPreviousCount = photo?.previousCount;
          }
          if (body?.documentType === 'WorkRequest') {
            ++workRequestPhotoRetryCount;
            workRequestPhotoPreviousCount = photo?.previousCount;
          }
        } else {
          throw new Error('photo not sync');
        }
        // throw new Error('Dsd');
      } catch (error) {
        const _photos = [...syncFailedAssets.photos];
        const findIndex = _photos.findIndex((photoMap) => photoMap.id === photo.id);
        if (findIndex > -1) {
          _photos[findIndex] = {
            ..._photos[findIndex],
            previousCount:
              photo?.documentType === 'Inspection'
                ? inspectionPhotoRetryCount + inspectionPhotoPreviousCount
                : photo?.documentType === 'Incident'
                ? incidentPhotoRetryCount + incidentPhotoPreviousCount
                : photo?.documentType === 'WorkOrder'
                ? workOrderPhotoRetryCount + workOrderPhotoPreviousCount
                : photo?.documentType === 'WorkRequest'
                ? workRequestPhotoRetryCount + workRequestPhotoPreviousCount
                : 0
          };
          dispatch?.(
            tasksListsActions.setSyncFailedAssets({
              photos: _photos,
              documents: documents
            })
          );
        }
        console.error('something wrong to send data');
      }
    }

    const transformData =
      groupPhotosByMobileSyncCountId[uniqueIdOfMobileSync]?.length > 0
        ? {
            ...(inspectionPhotoRetryCount && {
              completedInspectionPhotoCount: {
                op: 'Replace',
                value: inspectionPhotoRetryCount + inspectionPhotoPreviousCount
              }
            }),
            ...(workOrderPhotoRetryCount && {
              completedWorkOrderPhotoCount: {
                op: 'Replace',
                value: workOrderPhotoRetryCount + workOrderPhotoPreviousCount
              }
            }),
            ...(workRequestPhotoRetryCount && {
              completedWorkRequestPhotoCount: {
                op: 'Replace',
                value: workRequestPhotoRetryCount + workRequestPhotoPreviousCount
              }
            }),
            ...(incidentPhotoRetryCount && {
              completedIncidentPhotoCount: {
                op: 'Replace',
                value: incidentPhotoRetryCount + incidentPhotoPreviousCount
              }
            })
          }
        : null;

    if (transformData)
      await MobileSyncCountService.updateRequestToServerAfterSync({
        data: {
          ...transformData
        },
        idToStartSync: uniqueIdOfMobileSync,
        removeSyncId: false
      });
  }

  for (let uniqueIdOfMobileSync of Object.keys(groupDocumentByMobileSyncCountId)) {
    let inspectionDocumentRetryCount = 0,
      workOrderDocumentRetryCount = 0,
      workRequestDocumentRetryCount = 0,
      incidentDocumentRetryCount = 0,
      inspectionDocumentPreviousCount = 0,
      workOrderDocumentPreviousCount = 0,
      workRequestDocumentPreviousCount = 0,
      incidentDocumentPreviousCount = 0;

    for (let document of groupDocumentByMobileSyncCountId[uniqueIdOfMobileSync]) {
      const documentBase64 = await getAssetDataBase64InIndexDB({
        assetId: document?.content?.id
      });

      try {
        const {
          id: removeObj,
          old: removeOldField,
          previousCount: removeObj1,
          idToStartSync: removeObj2,
          ...body
        } = document;

        const documentApiResponse = await http.post(
          `/Documents`,
          {
            ...body,
            content: documentBase64
          },
          {
            timeout: 130000
          }
        );
        if (documentApiResponse?.status === 201) {
          failedDocumentCount++;
          documentIds.push(document.id);

          ++retryDocumentCount;
          incrementSyncTasksRecordsAndAssets({ dispatch });
          await deleteAssetInIndexDB({ assetId: document?.content?.id });

          if (body?.documentType === 'Inspection') {
            ++inspectionDocumentRetryCount;
            inspectionDocumentPreviousCount = document?.previousCount;
          }
          if (body?.documentType === 'Incident') {
            ++incidentDocumentRetryCount;
            incidentDocumentPreviousCount = document?.previousCount;
          }
          if (body?.documentType === 'WorkOrder') {
            ++workOrderDocumentRetryCount;
            workOrderDocumentPreviousCount = document?.previousCount;
          }
          if (body?.documentType === 'WorkRequest') {
            ++workRequestDocumentRetryCount;
            workRequestDocumentPreviousCount = document?.previousCount;
          }
        } else {
          throw new Error('document not sync');
        }
      } catch (error) {
        const _documents = [...syncFailedAssets.documents];
        const findIndex = _documents.findIndex((documentMap) => documentMap.id === document.id);
        if (findIndex > -1) {
          _documents[findIndex] = {
            ..._documents[findIndex],
            previousCount:
              document?.documentType === 'Inspection'
                ? inspectionDocumentRetryCount + inspectionDocumentPreviousCount
                : document?.documentType === 'Incident'
                ? incidentDocumentRetryCount + incidentDocumentPreviousCount
                : document?.documentType === 'WorkOrder'
                ? workOrderDocumentRetryCount + workOrderDocumentPreviousCount
                : document?.documentType === 'WorkRequest'
                ? workRequestDocumentRetryCount + workRequestDocumentPreviousCount
                : 0
          };
          dispatch?.(
            tasksListsActions.setSyncFailedAssets({
              photos: photos,
              documents: _documents
            })
          );
        }
        console.error('something wrong to send data');
      }
    }

    const transformData =
      groupDocumentByMobileSyncCountId[uniqueIdOfMobileSync]?.length > 0
        ? {
            ...(inspectionDocumentRetryCount && {
              completedInspectionDocumentCount: {
                op: 'Replace',
                value: inspectionDocumentRetryCount + inspectionDocumentPreviousCount
              }
            }),
            ...(workOrderDocumentRetryCount && {
              completedWorkOrderDocumentCount: {
                op: 'Replace',
                value: workOrderDocumentRetryCount + workOrderDocumentPreviousCount
              }
            }),
            ...(workRequestDocumentRetryCount && {
              completedWorkRequestDocumentCount: {
                op: 'Replace',
                value: workRequestDocumentRetryCount + workRequestDocumentPreviousCount
              }
            }),
            ...(incidentDocumentRetryCount && {
              completedIncidentDocumentCount: {
                op: 'Replace',
                value: incidentDocumentRetryCount + incidentDocumentPreviousCount
              }
            })
          }
        : null;

    if (transformData)
      await MobileSyncCountService.updateRequestToServerAfterSync({
        data: {
          ...transformData
        },
        idToStartSync: uniqueIdOfMobileSync,
        removeSyncId: false
      });
  }

  const { tasks } = store.getState();
  const { syncFailedAssets: updatedSyncFailedAssets } = tasks;
  await removeSuccessfullySyncedRetryAsset({
    documentIds,
    photoIds,
    syncFailedAssets: updatedSyncFailedAssets,
    dispatch
  });
};

const incrementSyncTasksRecordsAndAssets = ({ dispatch }) => {
  let data1 = {
    workOrder: workOrderNumber,
    workRequest: workRequestNumber,
    photos: syncPhotos,
    hseEvents: hseNumber,
    documents: syncDocument,
    inspection: inspectionNumber,
    inspectionPhotoCount,
    workOrderPhotoCount,
    workRequestPhotoCount,
    incidentPhotoCount,
    inspectionDocumentCount,
    workOrderDocumentCount,
    workRequestDocumentCount,
    incidentDocumentCount,
    failedDocumentCount,
    failedImageCount,

    editedEquipmentAssets: editedEquipmentAssets,
    newEquipmentAssets: newEquipmentAssets,

    checks: checkNumber,
    checkPhotoCount: checkPhotoCount,
    checkDocumentCount: checkDocumentCount
  };
  dispatch?.(tasksListsActions.setSyncDone(data1));
};

const syncNewEquipment = async ({
  transformData,
  dispatch,
  pendingSyncLength,
  doneNumber,
  sync,
  currentTask
}) => {
  if (currentTask?.pvIds?.length > 0) {
    let pvIds = currentTask?.pvIds;
    const attachPVIdsList = [];
    const detachPVIds = [];

    for (let pvId of pvIds) {
      const verificationStatus = pvId.verificationStatus || pvId.isDetached;
      const isDetached = ['attached', 'edited', 'new'].includes(verificationStatus) ? false : true;

      if (!isDetached) {
        attachPVIdsList.push(pvId.pvId);
      } else {
        detachPVIds.push(pvId.pvId);
      }
    }

    const [response] = await Promisable.asPromise(
      http.post(`/OnDemandItems`, {
        ...transformData,
        ...(attachPVIdsList?.length > 0 && { pvIds: attachPVIdsList }),
        ...(detachPVIds?.length > 0 && { detachPVIds: detachPVIds })
      })
    );
    const { data = {}, status } = response || {};

    if (!_.isEmpty(data) && status === 201) {
      const id = data;
      ++newEquipmentAssets;

      if (currentTask?.failedTask) {
        ++retryNewEquipmentAssetsCount;
      }

      incrementSyncTasksRecordsAndAssets({ dispatch });

      await EquipmentService.deleteNewOrExistingEquipment({
        equipmentId: id,
        siteId: transformData.siteId
      });

      dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));

      const pvIdsList = currentTask?.pvIds || [];
      for (let cat of pvIdsList) {
        deleteNewPSVRelation({ pvId: cat.pvId, psvId: cat?.psvId });
      }
      // deleteNewPSVRelation({ pvId: pvIds[0]?.pvId, psvId: pvIds[0]?.psvId });

      // if (pvIds?.length > 1) {
      //   const pvIdsList = currentTask.pvIds?.slice(1);
      //   for (let cat of pvIdsList) {
      //     const verificationStatus = cat.verificationStatus || cat.isDetached;

      //     const [response] = await Promisable.asPromise(
      //       http.post(`/OnDemandItems`, {
      //         ...transformData,
      //         pvId: cat.pvId,
      //         isDetached: ['attached', 'edited', 'new'].includes(verificationStatus) ? false : true,
      //         reconciliationRecordId: uuidv4()
      //       })
      //     );

      //     const { data = {}, status } = response || {};
      //     if (!_.isEmpty(data) && status === 201) {
      //       deleteNewPSVRelation({ pvId: cat.pvId, psvId: cat?.psvId });
      //     }
      //   }
      // }

      if (sync) {
        await EquipmentService.getAllNewAndUpdateSyncedEquipment({
          initialLogin: false,
          isSettingUpdated: false
        });
      }
    } else {
      dispatch?.(
        cacheActions.updateEquipmentWithFailedStatus({
          equipmentId: currentTask.equipmentId,
          data: {
            ...currentTask,
            failedTask: true
          }
        })
      );
    }
  } else {
    const [response] = await Promisable.asPromise(
      http.post(`/OnDemandItems`, {
        ...transformData
      })
    );
    const { data = {}, status } = response || {};
    if (!_.isEmpty(data) && status === 201) {
      const id = data;
      ++newEquipmentAssets;

      if (currentTask?.failedTask) {
        ++retryNewEquipmentAssetsCount;
      }

      incrementSyncTasksRecordsAndAssets({ dispatch });
      await EquipmentService.deleteNewOrExistingEquipment({
        equipmentId: id,
        siteId: transformData.siteId
      });
      transformData.jurisdiction &&
        dispatch?.(
          jurisdictionNumbersActions.deleteJurisdictionNumbers(transformData.jurisdiction)
        );
      dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));

      if (sync) {
        await EquipmentService.getAllNewAndUpdateSyncedEquipment({
          initialLogin: false,
          isSettingUpdated: false
        });
      }
    } else {
      dispatch?.(
        cacheActions.updateEquipmentWithFailedStatus({
          equipmentId: currentTask.equipmentId,
          data: {
            ...currentTask,
            failedTask: true
          }
        })
      );
    }
  }
};

const syncExistingEquipment = async ({
  currentTask,
  transformData,
  dispatch,
  pendingSyncLength,
  doneNumber,
  sync,
  jurisdictionNumbersMap,
  jurisdictionNumbers
}) => {
  if (currentTask?.pvIds?.length > 0) {
    const pvIds = currentTask?.pvIds;
    const attachPVIdsList = [];
    const detachPVIds = [];

    for (let pvId of pvIds) {
      const verificationStatus = pvId.verificationStatus || pvId.isDetached;
      const isDetached = ['attached', 'edited', 'new'].includes(verificationStatus) ? false : true;

      if (!isDetached) {
        attachPVIdsList.push(pvId.pvId);
      } else {
        detachPVIds.push(pvId.pvId);
      }
    }

    const [response] = await Promisable.asPromise(
      http.post(`/equipment/${transformData.equipmentId}/reconcile`, {
        ...transformData,
        ...(attachPVIdsList?.length > 0 && { pvIds: attachPVIdsList }),
        ...(detachPVIds?.length > 0 && { detachPVIds: detachPVIds })
      })
    );

    const { data = {}, status } = response || {};

    if (!_.isEmpty(data) && status === 201) {
      const id = transformData.equipmentId;
      ++editedEquipmentAssets;

      if (currentTask?.failedTask) {
        ++retryEditedEquipmentAssetsCount;
      }

      incrementSyncTasksRecordsAndAssets({ dispatch });

      EquipmentService.resetExistingOrExistingEquipment({ id });
      let transformJurisdiction = revertUpdatedJurisdictionNumbersListAndMap({
        jurisdictionNumbersMap,
        jurisdictionNumbers,
        equipmentId: transformData?.equipmentId
      });

      if (transformJurisdiction)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...transformJurisdiction
          })
        );

      const pvIdsList = currentTask?.pvIds || [];
      for (let cat of pvIdsList) {
        deleteNewPSVRelation({ pvId: cat.pvId, psvId: cat?.psvId });
      }

      dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));

      if (sync) {
        await EquipmentService.getAllNewAndUpdateSyncedEquipment({
          initialLogin: false,
          isSettingUpdated: false
        });
      }
    } else {
      dispatch?.(
        cacheActions.updateEquipmentWithFailedStatus({
          equipmentId: currentTask.equipmentId,
          data: {
            ...currentTask,
            failedTask: true
          }
        })
      );
    }
  } else {
    const [response] = await Promisable.asPromise(
      http.post(`/equipment/${transformData.equipmentId}/reconcile`, {
        ...transformData
      })
    );

    const { data = {}, status } = response || {};
    if (!_.isEmpty(data) && status === 201) {
      const id = transformData.equipmentId;
      ++editedEquipmentAssets;

      if (currentTask?.failedTask) {
        ++retryEditedEquipmentAssetsCount;
      }

      incrementSyncTasksRecordsAndAssets({ dispatch });

      EquipmentService.resetExistingOrExistingEquipment({ id });

      let transformJurisdiction = revertUpdatedJurisdictionNumbersListAndMap({
        jurisdictionNumbersMap,
        jurisdictionNumbers,
        equipmentId: transformData?.equipmentId
      });

      if (transformJurisdiction)
        dispatch?.(
          jurisdictionNumbersActions.updateEquipmentJurisdictionNumbers({
            ...transformJurisdiction
          })
        );

      dispatch?.(cacheActions.setPendingSync(pendingSyncLength - doneNumber));
      if (sync) {
        await EquipmentService.getAllNewAndUpdateSyncedEquipment({
          initialLogin: false,
          isSettingUpdated: false
        });
      }
    } else {
      dispatch?.(
        cacheActions.updateEquipmentWithFailedStatus({
          equipmentId: currentTask.equipmentId,
          data: {
            ...currentTask,
            failedTask: true
          }
        })
      );
    }
  }
};
